/**
 * Paginated fetch of story_cost_summary agent events (RAISE-16867 D3).
 *
 * Fetches from GET /api/v1/agent/events?event_type=story_cost_summary,
 * pages of 100, hard cap 5 pages / 500 events. Handles:
 * - 403 → planGated: true (team-plan required for no-ref listing)
 * - malformed payloads → skipped (parseStoryCostEvent returns null)
 * - truncation → truncated flag + total exposed for "showing N of M" note
 */

import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import {
  parseStoryCostEvent,
  dedupeEvents,
  type StoryCostEvent,
  type AgentEventItem,
} from "@/lib/cost/storyCostEvents";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentEventListResponse {
  events: AgentEventItem[];
  count: number;
  total: number;
}

export interface StoryCostEventsResult {
  events: StoryCostEvent[];
  loading: boolean;
  error: string | null;
  planGated: boolean;
  truncated: boolean;
  total: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 100;
const MAX_PAGES = 5;

// ---------------------------------------------------------------------------
// Fetch all pages (up to cap)
// ---------------------------------------------------------------------------

async function fetchAllStoryCostEvents(): Promise<{
  raw: AgentEventItem[];
  total: number;
  truncated: boolean;
}> {
  const collected: AgentEventItem[] = [];
  let total = 0;
  let offset = 0;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({
      event_type: "story_cost_summary",
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    const data = await api<AgentEventListResponse>(
      `/api/v1/agent/events?${params.toString()}`,
    );
    total = data.total;
    collected.push(...data.events);
    offset += data.events.length;

    // Stop if we've loaded everything
    if (offset >= total) break;
    // Stop if this page was short (server gave us fewer than PAGE_SIZE)
    if (data.events.length < PAGE_SIZE) break;
  }

  const truncated = total > collected.length;
  return { raw: collected, total, truncated };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch and parse story_cost_summary events for Views D and E.
 *
 * Results are deduped by (ref, sessionId) — addresses the RAISE-16865
 * double-emit scenario (session-close + story-close both emit for same session).
 *
 * sinceIso: ISO date string "YYYY-MM-DD". When provided, events whose effective
 * date (e.date ?? e.createdAt.slice(0,10)) is before this value are excluded.
 * The events endpoint has no since param — filtering is client-side (D1).
 */
export function useStoryCostEvents(sinceIso?: string): StoryCostEventsResult {
  const query = useQuery({
    queryKey: queryKeys.metrics.storyCostEvents({ eventType: "story_cost_summary" }),
    queryFn: fetchAllStoryCostEvents,
    // 403 must not trigger retry — it's a plan-gate, not a transient error
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 403) return false;
      return failureCount < 2;
    },
  });

  // 403 → planGated
  if (query.error instanceof ApiError && query.error.status === 403) {
    return {
      events: [],
      loading: false,
      error: null,
      planGated: true,
      truncated: false,
      total: 0,
    };
  }

  if (query.error) {
    return {
      events: [],
      loading: false,
      error:
        query.error instanceof Error
          ? query.error.message
          : "Failed to load cost events",
      planGated: false,
      truncated: false,
      total: 0,
    };
  }

  const raw = query.data?.raw ?? [];
  const total = query.data?.total ?? 0;
  const truncated = query.data?.truncated ?? false;

  // Parse, skip malformed, dedupe
  const parsed: StoryCostEvent[] = [];
  let skipped = 0;
  for (const item of raw) {
    const e = parseStoryCostEvent(item);
    if (e !== null) {
      parsed.push(e);
    } else {
      skipped++;
    }
  }
  if (skipped > 0) {
    console.debug(`useStoryCostEvents: skipped ${skipped} malformed events`);
  }

  const deduped = dedupeEvents(parsed);

  // Client-side date filter — events endpoint has no since param (D1)
  const events = sinceIso
    ? deduped.filter((e) => {
        const effectiveDate = e.date ?? e.createdAt.slice(0, 10);
        return effectiveDate >= sinceIso;
      })
    : deduped;

  return {
    events,
    loading: query.isLoading,
    error: null,
    planGated: false,
    truncated,
    total,
  };
}
